import { useCallback, useEffect, useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { ExamGridModel, FileModel, PatientModel } from 'models';

import { NotificationsService } from 'core/notifications';

import { useApiClient, useApiQuery } from 'features/api';
import { useAuthentication } from 'features/auth';
import { DocumentCategories } from 'features/exam/constants';
import { PreparedFile } from 'features/patient/fragments/PatientFiles.types';
// Assuming DocumentCategories is needed here
import { getDerivedTagNamesForFile } from 'features/patient/services/dynamicTagHelper';
import { getCategoryKeyById } from 'features/patient/services/file-category-helper';

dayjs.extend(utc);
dayjs.extend(timezone);

const EMPTY_GRID_DATA: ExamGridModel[] = [];

// Helper: Function to get category display name (copied from PatientFiles)
const getCategoryDisplayName = (file: FileModel): string | undefined => {
  const key = getCategoryKeyById(file.categoryId ?? null);
  return key ? DocumentCategories[key as keyof typeof DocumentCategories]?.name : 'Uncategorized';
};

// Helper: Prepare File Data (copied and adapted from PatientFiles)
const prepareFileData = (rawFiles: FileModel[], examGridData: ExamGridModel[]): PreparedFile[] => {
  const examMap = new Map(examGridData.map((exam) => [exam.id, exam]));
  return rawFiles.map((file) => {
    let sortableDate: Date;
    let displayDateString: string;

    const linkedExam = file.examId ? examMap.get(file.examId) : undefined;

    // Prioritize eventTime, then captureTime, then created for sortableDate
    if (file.eventTime) {
      // eventTime is already in local time, use directly
      const eventTime = dayjs(file.eventTime);
      sortableDate = eventTime.toDate();
      displayDateString = eventTime.format('MM/DD/YYYY HH:mm:ss');
    } else if (file.captureTime) {
      // captureTime is already in local time, use directly
      const captureTime = dayjs(file.captureTime);
      sortableDate = captureTime.toDate();
      displayDateString = captureTime.format('MM/DD/YYYY HH:mm:ss');
    } else if (file.created) {
      // created is in Pacific time, convert to local time
      const pacificTime = dayjs.tz(file.created, 'America/Los_Angeles');
      sortableDate = pacificTime.toDate();
      displayDateString = pacificTime.local().format('MM/DD/YYYY HH:mm:ss');
    } else {
      sortableDate = new Date(0);
      displayDateString = 'No date';
    }

    const assignedTagIds = file.tags?.map((t) => t.id) ?? [];
    const derivedTagNames = getDerivedTagNamesForFile(file, linkedExam);

    return {
      ...file,
      sortableDate,
      displayDateString,
      categoryDisplayName: getCategoryDisplayName(file),
      tagIds: assignedTagIds,
      derivedTagNames: derivedTagNames,
    };
  });
};

interface UsePatientFileDataResult {
  patient: PatientModel | null;
  files: FileModel[]; // Raw files
  preparedFiles: PreparedFile[]; // Prepared files
  isLoading: boolean;
  fetchData: () => Promise<void>; // Expose fetchData for manual refresh
}

export const usePatientFileData = (patientId: number, gridData: ExamGridModel[] = EMPTY_GRID_DATA, disableDataFetching = false): UsePatientFileDataResult => {
  const { activeScheme } = useAuthentication();
  const apiClient = useApiClient();

  // Simply use gridData directly - React should handle reference equality properly
  const stableGridData = gridData;

  // Use authenticated API query hooks
  const {
    data: patient,
    isLoading: isLoadingPatient,
    refetch: refetchPatient,
    error: patientError,
  } = useApiQuery({
    queryKey: ['patient'],
    apiFn: apiClient.patientClient.getPatient as (...args: unknown[]) => Promise<unknown>,
    params: [patientId],
    skipIfInvalidParams: false, // Don't skip if patientId is 0 - it might be valid
    skipCondition: () => disableDataFetching, // Skip API call if data fetching is disabled
  });

  console.log('[usePatientFileData] About to call files query with:', {
    patientId,
    activeScheme,
    apiClientExists: !!apiClient,
    filesClientExists: !!apiClient?.filesClient,
    getFilesByPatientIdExists: !!apiClient?.filesClient?.getFilesByPatientId,
    disableDataFetching,
  });

  const {
    data: files = [],
    isLoading: isLoadingFiles,
    refetch: refetchFiles,
    error: filesError,
  } = useApiQuery({
    queryKey: ['files', 'by-patient-id'],
    apiFn: apiClient.filesClient.getFilesByPatientId as (...args: unknown[]) => Promise<unknown>,
    params: [patientId],
    skipIfInvalidParams: false, // Don't skip if patientId is 0 - it might be valid
    skipCondition: () => disableDataFetching, // Skip API call if data fetching is disabled
    retry: (failureCount, error) => {
      // Retry auth errors up to 3 times in case auth setup is still in progress
      if (error?.message?.includes('getAccessToken has not been set') && failureCount < 3) {
        console.log(`[usePatientFileData] Retrying files query due to auth error (attempt ${failureCount + 1})`);
        return true;
      }
      return failureCount < 1; // Normal retry logic for other errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff up to 3 seconds
  });

  console.log('[usePatientFileData] Files query result:', {
    files: (files as FileModel[] | undefined)?.length || 0,
    isLoadingFiles,
    filesError: filesError?.message,
    patientId,
    disableDataFetching,
  });

  // Prepare files when raw files or gridData changes - use stable gridData
  const preparedFiles = useMemo(() => {
    // If data fetching is disabled, return empty array
    if (disableDataFetching) return [];

    const typedFiles = files as FileModel[] | undefined;
    console.log('[usePatientFileData] preparedFiles useMemo triggered:', {
      filesLength: typedFiles?.length || 0,
      stableGridDataLength: stableGridData.length,
      disableDataFetching,
    });

    if (!typedFiles || typedFiles.length === 0) return [];

    const processedFiles = typedFiles.map((file: FileModel) => ({
      ...file,
      externalSource: file.externalSource === undefined ? null : file.externalSource,
    }));

    return prepareFileData(processedFiles, stableGridData);
  }, [files, stableGridData, disableDataFetching]);

  // Handle errors
  useEffect(() => {
    if (patientError) {
      console.error('[usePatientFileData] Failed to fetch patient data:', patientError);
      NotificationsService.displayError('Failed to load patient data.');
    }
  }, [patientError]);

  useEffect(() => {
    if (filesError) {
      console.error('[usePatientFileData] Failed to fetch patient files:', filesError);
      NotificationsService.displayError('Failed to load patient files.');
    }
  }, [filesError]);

  // Manual refresh function for backward compatibility
  const fetchData = useCallback(async () => {
    console.log('[usePatientFileData] Running fetchData (refetching queries)');

    // Don't make API calls if authentication is not initialized
    if (activeScheme === undefined) {
      console.log('[usePatientFileData] Skipping fetch - authentication not initialized');
      return;
    }

    try {
      await Promise.all([refetchPatient(), refetchFiles()]);
    } catch (error) {
      console.error('[usePatientFileData] Failed to refetch data:', error);
      // Errors are already handled by the individual query error effects above
    }
  }, [refetchPatient, refetchFiles, activeScheme]);

  const isLoading = isLoadingPatient || isLoadingFiles;

  return {
    patient: (patient as PatientModel) || null,
    files: (files as FileModel[]) || [],
    preparedFiles,
    isLoading,
    fetchData,
  };
};
